package top.wcpe.allincore.ui.runtime.ir.api

import top.wcpe.allincore.ui.runtime.host.UiBiFunction
import top.wcpe.allincore.ui.runtime.host.UiFunction
import top.wcpe.allincore.ui.runtime.reactive.Signal
import kotlin.jvm.JvmField
import kotlin.jvm.JvmStatic

/**
 * DSL 展示物品读取 API：`display.read(key, index)` 读取服务端直推的玩家维度展示物品，
 * `display.size(key)` 响应式读取该 key 当前的物品数量（供 `for (i in 0 until ...)` 按实际数量渲染）。
 *
 * 与 [SlotApi] 的区别：展示物品不依赖容器 session / 槽位注册，按 `<namespace>.<name>` key +
 * 下标全局寻址，纯只读展示。返回 [ItemView]（包裹响应式信号）——`item` prop 的 Effect 在
 * 应用视图时读信号即建立订阅，数据推达后写信号自动触发重求值重渲染。
 */
interface DisplayApi {

    /** 读取 key 下第 index 个展示物品视图；无数据时返回空视图（isEmpty = true）。 */
    fun read(key: String, index: Int): ItemView

    /** 读取 key 下当前物品数量；无数据时返回 0。Effect 内调用即订阅数量信号，推送后自动重求值。 */
    fun size(key: String): Int

    companion object {
        /** 空实现：任何读取都返回空视图 / 数量 0（服务端 / 未接线环境）。 */
        @JvmField val NOOP: DisplayApi = object : DisplayApi {
            override fun read(key: String, index: Int): ItemView = ItemView.EMPTY
            override fun size(key: String): Int = 0
        }

        /**
         * 由信号提供器构造实现：read 向 (key, index) → Signal 提供器要信号并包成 [ItemView]；
         * size 向 key → Signal 提供器要数量信号并当场 `get()`——在 Effect 内求值即建立订阅，
         * 数量变化后写信号自动触发重求值。信号实例均由提供器负责缓存复用——同 key（/ 同
         * (key, index)）必须恒返回同一 Signal，守住响应式绑定约束（同 SlotApi 的 signalProvider 契约）。
         */
        @JvmStatic
        fun of(
            signalProvider: UiBiFunction<String, Int, Signal<ClientItemStack>>?,
            sizeProvider: UiFunction<String, Signal<Int>>?,
        ): DisplayApi {
            if (signalProvider == null || sizeProvider == null) return NOOP
            return object : DisplayApi {
                override fun read(key: String, index: Int): ItemView = ItemView.of(signalProvider.apply(key, index))
                override fun size(key: String): Int = sizeProvider.apply(key).get()
            }
        }
    }
}
